import { Controller } from "@hotwired/stimulus"
import { DateTime } from "luxon"
import DirectoryTable from "../lib/directory_table"
import { columnPersistenceOptions, columnVisibilityHeaderMenu, emptyStatePlaceholder, emptyStateFor } from "./tabulator_helpers"

export default class extends Controller {
  static targets = [
    'contacts',
    'table',
    'queryTitleBanner',
    'queryTitleContent'
  ]

  static values = {
    url: String,
    tabulatorTable: Object,
    metadataKeys: Array,
    filtersOpen: Boolean,
    hasAccountNumbers: Boolean,
    userNameMap: Object,
  }
  boundDownloadCsv = this.downloadCsv.bind(this)
  activityRequests = new Set()

  // Utility function to clean URL parameters
  cleanUrlParams(url) {
    const cleanUrl = new URL(url)
    cleanUrl.searchParams.delete('all_open')
    cleanUrl.searchParams.delete('all_on_hold')
    return cleanUrl
  }

  calculateTableHeight() {
    const headerElement = document.querySelector('.section-header-slim, .section-header')
    const filtersElement = document.querySelector('.section-filters')
    const mobileHeaderElement = document.querySelector('header.mobile') // Select the mobile-only header
    const queryTitleBannerElement = this.queryTitleBannerTarget

    // Calculate heights, checking for visibility
    const headerHeight = headerElement ? headerElement.offsetHeight : 0
    const filtersHeight = (filtersElement && window.getComputedStyle(filtersElement).display !== 'none') ? filtersElement.offsetHeight - 6 : 0
    const mobileHeaderHeight = (mobileHeaderElement && window.getComputedStyle(mobileHeaderElement).display !== 'none') ? mobileHeaderElement.offsetHeight : 0
    const queryTitleBannerHeight = (queryTitleBannerElement && !queryTitleBannerElement.classList.contains('hidden')) ? queryTitleBannerElement.offsetHeight : 0;

    // Combine all visible header heights
    const combinedHeight = mobileHeaderHeight + headerHeight + filtersHeight + queryTitleBannerHeight;
    return `max(240px, calc(100vh - ${combinedHeight}px))`
  }

  // Fetch sparkline data for the currently visible rows and fill the Activity
  // column once it arrives. Runs after the table is already on screen.
  // Requests are chunked so large page sizes (up to 1000 rows) stay under
  // proxy URL-length limits and each backend query stays small.
  loadActivity(references) {
    const wanted = (references || []).filter(Boolean)
    if (!wanted.length) {
      return
    }

    const chunkSize = 150
    const table = this.tabulatorTable
    for (let i = 0; i < wanted.length; i += chunkSize) {
      const chunk = wanted.slice(i, i + chunkSize)
      const params = new URLSearchParams()
      chunk.forEach((reference) => params.append("references[]", reference))
      const request = new AbortController()
      this.activityRequests.add(request)

      fetch(`/contacts/activity?${params.toString()}`, {
        headers: { "Accept": "application/json" },
        signal: request.signal,
      })
        .then((response) => (response.ok ? response.json() : {}))
        .catch(() => ({}))
        .then((activityByReference) => {
          if (request.signal.aborted || this.tabulatorTable !== table) return
          // Resolve every requested reference — even on error or a missing
          // entry — so the loading skeleton always clears to a blank cell.
          const updates = chunk.map((reference) => {
            const series = activityByReference[reference]
            return { reference, activity: Array.isArray(series) ? series : [] }
          })
          return table.updateData(updates).catch(() => {})
        })
        .finally(() => this.activityRequests.delete(request))
    }
  }

  // A stored value ("verified", "business") as a readable filter link
  // ("Verified"); the link narrows the list to that value. Built as an element
  // so the value is text, never markup.
  valueFilterLink(param, value, label = null) {
    if (!value) return ""
    const url = this.cleanUrlParams(window.location.href)
    url.searchParams.set(param, value)
    const link = document.createElement("a")
    link.href = url.toString()
    link.className = "link"
    link.setAttribute("data-turbo-prefetch", "false")
    const words = String(value).replace(/_/g, " ")
    link.textContent = label || words.charAt(0).toUpperCase() + words.slice(1)
    return link
  }

  connect() {
    this.queryTitleBannerTarget.classList.add('hidden') // Ensure banner is hidden initially

    // Build columns array, conditionally including account_number
    const columns = [
      { title: "Reference", field: "reference", formatter: "link", frozen: false, width: 130, minWidth: 118,
        formatterParams: {
        url: (cell) => {
          return `/contacts/${cell.getRow().getData().reference}`
        },
      }},
      { title: "Display name", field: "display_name", formatter: "link", width: 280, minWidth: 220, widthGrow: 2, formatterParams: {
        url: (cell) => {
          return `/contacts/${cell.getRow().getData().reference}`
        },
      }},
      {
        title: "Activity",
        field: "activity",
        headerSort: "remote",
        download: false,
        // Wide enough for the title and its sort arrow: column heads never truncate.
        width: 116,
        minWidth: 116,
        headerHozAlign: "center",
        hozAlign: "center",
        vertAlign: "middle",
        formatter: (cell) => {
          const counts = cell.getRow().getData().activity
          // undefined means the lazy activity fetch hasn't resolved yet;
          // loadActivity sets [] once it has, even for empty/error results.
          // Minimal placeholder: faint gray ticks in the sparkline's own
          // geometry, gently pulsing — quieter than a skeleton block.
          if (counts === undefined) {
            const ticks = Array.from({ length: 12 }, (_, i) =>
              `<rect x="${i * 4}" y="13" width="3" height="3" rx="0.5" fill="currentColor"/>`
            ).join("")
            return `<div class="animate-pulse" style="height:100%;display:flex;align-items:center;opacity:0.15;"><svg width="47" height="16" viewBox="0 0 47 16" aria-hidden="true">${ticks}</svg></div>`
          }
          if (!counts || !counts.length) {
            return ""
          }
          const max = Math.max(...counts)
          if (max === 0) {
            return ""
          }
          const total = counts.reduce((sum, count) => sum + count, 0)
          const barWidth = 3, gap = 1, height = 16
          const bars = counts.map((count, i) => {
            const barHeight = count === 0 ? 1 : Math.max((count / max) * height, 2)
            const opacity = count === 0 ? 0.18 : 1
            return `<rect x="${i * (barWidth + gap)}" y="${(height - barHeight).toFixed(1)}" width="${barWidth}" height="${barHeight.toFixed(1)}" rx="0.5" fill="#EA473C" opacity="${opacity}"/>`
          }).join("")
          const width = counts.length * (barWidth + gap) - gap
          return `<div style="height:100%;display:flex;align-items:center;"><svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" aria-hidden="true"><title>${total} events in the last 12 weeks</title>${bars}</svg></div>`
        }
      },
    ]

    // Only include Account # column if some contacts have account numbers
    if (this.hasAccountNumbersValue) {
      columns.push({ 
        title: "Account #", 
        field: "account_number", 
        width: 140,
        minWidth: 120,
        formatter: (cell) => {
          if (!cell.getValue()) {
            return "";
          }
          const currentUrl = this.cleanUrlParams(window.location.href);
          currentUrl.searchParams.set('account_number', cell.getValue());
          return `<a href="${currentUrl.toString()}" class="link"><code>${cell.getValue()}</code></a>`;
        }
      })
    }

    columns.push(
      {
        title: "Assigned user",
        field: "assigned_user_id",
        width: 175,
        minWidth: 150,
        formatter: (cell) => {
          if (!cell.getValue()) {
            return "";
          }
          const currentUrl = this.cleanUrlParams(window.location.href);
          currentUrl.searchParams.set('assigned_user_id', cell.getValue());
          const userName = this.userNameMapValue[cell.getValue()] || cell.getValue();
          return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${userName}</a>`;
        }
      },
      { 
        title: "Status", 
        field: "contact_status", 
        width: 115,
        minWidth: 100,
        formatter: (cell) => this.valueFilterLink('contact_status', cell.getValue())
      },
      { 
        title: "Type", 
        field: "contact_type", 
        width: 110,
        minWidth: 95,
        formatter: (cell) => this.valueFilterLink('contact_type', cell.getValue())
      },
      { 
        title: "Classification", 
        field: "contact_classification", 
        width: 160,
        minWidth: 140,
        formatter: (cell) => this.valueFilterLink('contact_classification', cell.getValue(), cell.getRow().getData().contact_classification_name)
      },
      { 
        title: "External ID", 
        field: "external_id", 
        width: 145,
        minWidth: 120,
        formatter: (cell) => {
          if (!cell.getValue()) {
            return "";
          }
          const currentUrl = this.cleanUrlParams(window.location.href);
          currentUrl.searchParams.set('external_id', cell.getValue());
          return `<a href="${currentUrl.toString()}" class="link">${cell.getValue()}</a>`;
        }
      },
      { title: "Created", field: "created_at", width: 190, minWidth: 170, formatter: "datetime", formatterParams: {
        inputFormat: "iso",
        outputFormat: "MMM d, yy 'at' t",
        invalidPlaceholder: "(invalid date)",
        timezone: "America/Los_Angeles",
      }},
      { title: "Updated", field: "updated_at", width: 190, minWidth: 170, formatter: "datetime", formatterParams: {
        inputFormat: "iso",
        outputFormat: "MMM d, yy 'at' t",
        invalidPlaceholder: "(invalid date)",
        timezone: "America/Los_Angeles",
      }},
    )

    this.tabulatorTable = new DirectoryTable(this.tableTarget, {
      layout: 'fitDataFill',
      ...columnPersistenceOptions("contacts-table"),
      index: "reference",
      columnDefaults: {
        minWidth: 90,
        headerMenu: columnVisibilityHeaderMenu,
      },
      placeholder: emptyStateFor("contacts"),
      pagination: true,
      paginationMode: 'remote',
      sortMode: 'remote',
      initialSort: [
        { column: "activity", dir: "desc" },
      ],
      paginationSize: 100,
      paginationSizeSelector: [100, 250, 500, 1000, true],
      height: this.calculateTableHeight(),
      renderHorizontal: "virtual",
      dataSendParams: {
        "page": "page",
        "size": "length",
        "sorters": "sort",
      },
      ajaxURL: this.urlValue,
      ajaxResponse: (url, params, response) => {
        const bannerText = [response.query_title, response.search_notice].filter(Boolean).join(' - ')
        if (bannerText) {
          this.queryTitleContentTarget.textContent = bannerText
          this.queryTitleBannerTarget.classList.remove('hidden')
        } else {
          this.queryTitleBannerTarget.classList.add('hidden')
        }
        this.tabulatorTable.setHeight(this.calculateTableHeight());

        // Activity sparklines are loaded separately so the table renders
        // without waiting on the per-page activity query.
        this.loadActivity(
          response.contacts
            .filter((contact) => contact.activity === undefined)
            .map((contact) => contact.reference)
        )

        return {
          data: response.contacts,
          last_page: response.last_page,
          last_row: response.last_row
        }
      },
      columns: columns,
      dependencies: {
        DateTime: DateTime,
      },  
    })

    // #download_csv
    this.downloadButton = document.getElementById('download_csv')
    this.downloadButton?.addEventListener('click', this.boundDownloadCsv)

    this.tabulatorTable.on("rowDblClick", function(e, row){
      window.location.href = `/contacts/${row.getData().reference}`
    });
  }

  downloadCsv() {
    this.tabulatorTable?.download('csv', `contacts-${DateTime.now().toFormat('yyyy-MM-dd-HH-mm')}.csv`)
  }

  disconnect() {
    this.downloadButton?.removeEventListener('click', this.boundDownloadCsv)
    this.downloadButton = null
    this.activityRequests.forEach(request => request.abort())
    this.activityRequests.clear()
    this.tabulatorTable?.destroy()
    this.tabulatorTable = null
  }
}
